<template>
  <div class="control-room min-h-screen">
    <div class="mx-auto max-w-7xl px-4 pb-12 pt-8 sm:px-6 lg:px-8">
      <!-- Header: the reference puts liveness, the window and the sync stamp on one line,
           because all three describe how fresh and how wide the numbers are. -->
      <header class="flex flex-wrap items-end justify-between gap-6">
        <div class="min-w-0">
          <p class="cr-label">Mail authentication · control room</p>
          <h1 class="mt-2 text-3xl font-semibold tracking-tight text-gray-900">DMARC dashboard</h1>
          <p class="mt-2 max-w-xl text-sm text-gray-500">
            Volume, protocol pass rates and policy outcomes, all read from the same window.
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-x-4 gap-y-3">
          <p class="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
            <span class="relative flex h-1.5 w-1.5">
              <span class="absolute inline-flex h-full w-full rounded-full bg-success-500 opacity-60 motion-safe:animate-ping"></span>
              <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-success-500"></span>
            </span>
            Live
            <span class="text-gray-300" aria-hidden="true">·</span>
            <span class="normal-case tracking-normal text-gray-500 nums">
              {{ syncedAt ? `Synced ${syncedAt}` : 'Syncing…' }}
            </span>
          </p>
          <div class="flex items-center gap-2">
            <RangeSelector v-model="range" />
            <button type="button" class="cr-icon-button" aria-label="Refresh dashboard" title="Refresh" @click="load">
              <svg class="h-4 w-4" :class="{ 'animate-spin': loading }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <!-- Loading -->
      <div v-if="loading && !summary" class="flex items-center justify-center py-24">
        <div class="flex items-center gap-3">
          <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
          <span class="text-sm text-gray-500">Loading telemetry…</span>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="cr-card mt-8 border-danger-200 bg-danger-50 p-5">
        <h2 class="text-sm font-semibold text-danger-800">The dashboard could not be loaded</h2>
        <p class="mt-1 text-sm text-danger-700">{{ error }}</p>
        <button type="button" class="btn btn-secondary mt-4 text-sm" @click="load">Try again</button>
      </div>

      <template v-else-if="summary && hasReports">
        <!-- Trust index + stat tiles -->
        <div class="mt-8 grid grid-cols-1 gap-5 xl:grid-cols-12">
          <section class="cr-card flex items-center p-6 xl:col-span-7">
            <div class="flex flex-col items-center gap-8 sm:flex-row">
              <TrustGauge :dmarc="dmarcRate" :dkim="dkimRate" />

              <div class="min-w-0 flex-1">
                <p class="cr-label">Protocol layers</p>
                <dl class="mt-2 divide-y divide-gray-100">
                  <div v-for="layer in layers" :key="layer.key" class="flex items-center gap-3 py-2.5">
                    <span class="h-1.5 w-1.5 shrink-0 rounded-full" :style="{ backgroundColor: layer.color }"></span>
                    <dt class="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-600">
                      {{ layer.label }}
                    </dt>
                    <span class="h-px flex-1 border-b border-dotted border-gray-300"></span>
                    <dd class="text-sm font-semibold nums" :class="rateTone(layer.value)">
                      {{ layer.value }}%
                    </dd>
                  </div>
                </dl>

                <div class="mt-4 flex items-start gap-2.5 rounded-xl border px-3.5 py-3" :class="insight.classes">
                  <svg v-if="insight.tone === 'ok'" class="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  <svg v-else class="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3.75m0 3.75h.008M10.34 3.94l-7.5 13a1.5 1.5 0 0 0 1.3 2.25h15a1.5 1.5 0 0 0 1.3-2.25l-7.5-13a1.5 1.5 0 0 0-2.6 0Z" />
                  </svg>
                  <p class="text-sm">{{ insight.text }}</p>
                </div>
              </div>
            </div>
          </section>

          <div class="grid auto-rows-fr grid-cols-2 gap-5 xl:col-span-5">
            <article v-for="tile in tiles" :key="tile.label" class="cr-card flex flex-col p-5">
              <p class="cr-label min-h-[2rem] leading-4">{{ tile.label }}</p>
              <p class="mt-2 text-4xl font-semibold leading-none tracking-tight nums text-gray-900">
                {{ tile.value }}<span v-if="tile.suffix" class="ml-1 text-lg font-medium text-gray-400">{{ tile.suffix }}</span>
              </p>
              <div class="mt-auto pt-5">
                <div class="h-1 w-full overflow-hidden rounded-full bg-gray-200">
                  <span
                    v-if="tile.meter !== null"
                    class="block h-full rounded-full"
                    :style="{ width: `${tile.meter}%`, backgroundColor: tile.color }"
                  ></span>
                </div>
                <p class="mt-2 text-[11px] font-medium uppercase tracking-[0.12em] text-gray-500">
                  {{ tile.footnote }}
                </p>
              </div>
            </article>
          </div>
        </div>

        <!-- Telemetry -->
        <section class="cr-card mt-5 p-5 sm:p-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p class="cr-label">Traffic telemetry · {{ rangeLabel }}</p>
              <h2 class="mt-1.5 text-lg font-medium text-gray-900">Volume and authentication breaches</h2>
            </div>
            <div class="flex flex-wrap items-center gap-4 text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
              <span class="flex items-center gap-2">
                <span class="h-px w-5" :style="{ backgroundColor: 'rgb(var(--cr-beam))' }"></span>
                Messages / day
              </span>
              <span class="flex items-center gap-2">
                <span class="h-2.5 w-px" :style="{ backgroundColor: 'rgb(var(--cr-flare))' }"></span>
                Failure spike
              </span>
            </div>
          </div>

          <TelemetryChart class="mt-2" :points="trendPoints" :height="260" />
        </section>

        <!-- Policy, reporters, sources -->
        <div class="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          <section class="cr-card flex flex-col p-5 sm:p-6">
            <p class="cr-label">Policy enforcement</p>
            <div class="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <p class="text-5xl font-semibold leading-none tracking-tight nums text-gray-900">
                {{ policy.nonePct }}<span class="text-xl font-medium text-gray-400">%</span>
              </p>
              <p class="text-sm text-gray-500">
                delivered under <code class="font-mono text-xs text-gray-600">p=none</code>
              </p>
            </div>

            <div
              class="mt-5 flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100"
              role="img"
              :aria-label="`Policy distribution: ${policy.segments.map((segment) => `${segment.label} ${segment.pct} percent`).join(', ')}`"
            >
              <span
                v-for="segment in policy.segments"
                :key="segment.key"
                class="h-full"
                :style="{ width: `${segment.pct}%`, backgroundColor: segment.color }"
              ></span>
            </div>

            <dl class="mt-3 grid grid-cols-3 gap-2">
              <div v-for="segment in policy.segments" :key="segment.key" class="min-w-0">
                <dt class="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-gray-500">
                  <span class="h-1.5 w-1.5 shrink-0 rounded-full" :style="{ backgroundColor: segment.color }"></span>
                  <span class="truncate">{{ segment.label }}</span>
                </dt>
                <dd class="mt-0.5 text-sm nums text-gray-900">{{ segment.pct }}%</dd>
              </div>
            </dl>

            <div class="mt-auto pt-5">
              <div class="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-4">
                <p class="cr-label">Perimeter checks</p>
                <router-link to="/domains" class="cr-link">Domains ↗</router-link>
              </div>
              <div class="mt-2 flex flex-wrap gap-2">
                <span class="cr-chip">
                  <span class="h-1.5 w-1.5 rounded-full bg-success-500"></span>OK {{ domains?.ok ?? 0 }}
                </span>
                <span class="cr-chip">
                  <span class="h-1.5 w-1.5 rounded-full bg-warning-500"></span>Warn {{ domains?.warn ?? 0 }}
                </span>
                <span class="cr-chip">
                  <span class="h-1.5 w-1.5 rounded-full bg-danger-500"></span>Error {{ domains?.error ?? 0 }}
                </span>
              </div>
            </div>
          </section>

          <section class="cr-card flex flex-col p-5 sm:p-6">
            <div class="flex items-baseline justify-between gap-3">
              <p class="cr-label">Reporting orgs</p>
              <router-link to="/reports" class="cr-link">All ↗</router-link>
            </div>

            <dl v-if="topSources.length" class="mt-4 space-y-3.5">
              <div v-for="org in topSources" :key="org.orgName">
                <div class="flex items-baseline justify-between gap-3">
                  <dt class="truncate text-sm text-gray-700">{{ org.orgName }}</dt>
                  <dd class="shrink-0 text-sm nums text-gray-900">{{ org.messageCount.toLocaleString() }}</dd>
                </div>
                <div class="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <span
                    class="block h-full rounded-full"
                    :style="{ width: `${share(org.messageCount, maxOrgMessages)}%`, backgroundColor: 'rgb(var(--cr-beam))' }"
                  ></span>
                </div>
              </div>
            </dl>
            <p v-else class="mt-4 text-sm text-gray-500">No reports in this window.</p>
          </section>

          <section class="cr-card flex flex-col p-5 sm:p-6">
            <div class="flex items-baseline justify-between gap-3">
              <p class="cr-label">Source nodes</p>
              <router-link to="/sources" class="cr-link">All ↗</router-link>
            </div>

            <ul v-if="topIPs.length" class="mt-2 divide-y divide-gray-100">
              <li v-for="ip in topIPs" :key="ip.sourceIp" class="flex items-center gap-3 py-2.5">
                <span
                  class="h-1.5 w-1.5 shrink-0 rounded-full"
                  :class="ip.failureRate === 0 ? 'bg-success-500' : ip.failureRate < 10 ? 'bg-warning-500' : 'bg-danger-500'"
                ></span>
                <p class="min-w-0 flex-1 truncate font-mono text-sm text-gray-900">{{ ip.sourceIp }}</p>
                <p class="shrink-0 text-xs nums text-gray-500">{{ ip.messageCount.toLocaleString() }}</p>
                <span class="cr-chip shrink-0">{{ ip.failureRate }}% fail</span>
              </li>
            </ul>
            <p v-else class="mt-4 text-sm text-gray-500">No source IPs in this window.</p>
          </section>
        </div>

        <!-- Attention + spoofing -->
        <div class="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <section class="cr-card p-5 sm:p-6">
            <div class="flex items-baseline justify-between gap-3">
              <p class="cr-label">Needs attention</p>
              <router-link to="/domains" class="cr-link">Domains ↗</router-link>
            </div>

            <ul v-if="needsAttention.length" class="mt-3 divide-y divide-gray-100">
              <li v-for="domain in needsAttention" :key="domain.id" class="flex items-start justify-between gap-3 py-2.5">
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium text-gray-900">{{ domain.name }}</p>
                  <p class="truncate text-xs text-gray-500">{{ domain.problems[0] || 'The last check failed' }}</p>
                </div>
                <StatusBadge :status="domain.status" kind="domain" />
              </li>
            </ul>
            <p v-else class="mt-4 flex items-center gap-2 text-sm text-gray-500">
              <svg class="h-4 w-4 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              Every monitored domain passed its last check.
            </p>
          </section>

          <section class="cr-card p-5 sm:p-6">
            <div class="flex items-baseline justify-between gap-3">
              <p class="cr-label">Top spoofing sources</p>
              <router-link to="/forensics" class="cr-link">Forensics ↗</router-link>
            </div>

            <ul v-if="spoofingSources.length" class="mt-3 divide-y divide-gray-100">
              <li v-for="source in spoofingSources" :key="source.sourceIp" class="flex items-center gap-3 py-2.5">
                <p class="min-w-0 flex-1 truncate font-mono text-sm text-gray-900">{{ source.sourceIp }}</p>
                <span class="cr-chip shrink-0">{{ source.count }} report{{ source.count === 1 ? '' : 's' }}</span>
              </li>
            </ul>
            <p v-else class="mt-4 text-sm text-gray-500">
              No forensic reports in this window — the ruf= channel was quiet.
            </p>
          </section>
        </div>
      </template>

      <!-- Empty state. The API answers with a zeroed summary rather than null when there is
           nothing yet, so `summary` alone cannot decide this. -->
      <EmptyState
        v-else
        icon="chart"
        :title="range === 'all' ? 'No DMARC data available' : 'No reports in this window'"
        :description="
          range === 'all'
            ? 'Start by processing some DMARC reports to see your dashboard metrics.'
            : 'Nothing arrived in the selected range. Try a wider window.'
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import TrustGauge from '@/components/TrustGauge.vue';
import TelemetryChart from '@/components/TelemetryChart.vue';
import RangeSelector from '@/components/RangeSelector.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import EmptyState from '@/components/EmptyState.vue';
import { summaryApi, forensicsApi, domainsApi } from '@/api/client';
import type {
  DashboardStats,
  DomainHealthSummary,
  ForensicSummary,
  StatsRange,
} from '@/types/api';
import type { TelemetryPoint } from '@/types/charts';

// The window is part of the request, not a client-side filter: every figure on this page
// (totals, pass rates, buckets) describes the same range.
const range = ref<StatsRange>('30d');

const summary = ref<DashboardStats | null>(null);
const forensic = ref<ForensicSummary | null>(null);
const domains = ref<DomainHealthSummary | null>(null);

const loading = ref(true);
const error = ref<string | null>(null);
const syncedAt = ref<string | null>(null);

/**
 * One load per range. The summary is required; the forensic and domain summaries are
 * supporting figures, and an empty ruf mailbox or a fresh install must not turn the whole
 * dashboard into an error.
 */
let requestToken = 0;

const load = async () => {
  // Two quick range clicks can land out of order; only the newest request may write.
  const token = ++requestToken;
  loading.value = true;
  error.value = null;

  try {
    const [summaryResult, forensicResult, domainResult] = await Promise.allSettled([
      summaryApi.getSummary(range.value),
      forensicsApi.summary(),
      domainsApi.summary(),
    ]);

    if (token !== requestToken) return;

    if (summaryResult.status === 'rejected') {
      throw summaryResult.reason;
    }

    summary.value = summaryResult.value;
    forensic.value = forensicResult.status === 'fulfilled' ? forensicResult.value : null;
    domains.value = domainResult.status === 'fulfilled' ? domainResult.value : null;
    syncedAt.value = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch (cause) {
    if (token === requestToken) {
      error.value = cause instanceof Error ? cause.message : 'An unexpected error occurred.';
    }
  } finally {
    if (token === requestToken) loading.value = false;
  }
};

load();
watch(range, load);

const hasReports = computed(() => (summary.value?.totalReports ?? 0) > 0);

const dmarcRate = computed(() => Math.round(summary.value?.avgDmarcPassRate ?? 0));
const dkimRate = computed(() => Math.round(summary.value?.avgDkimPassRate ?? 0));
const spfRate = computed(() => Math.round(summary.value?.avgSpfPassRate ?? 0));

const rangeLabel = computed(
  () => ({ '7d': '7 days', '30d': '30 days', '90d': '90 days', all: 'all time' })[range.value],
);

const layers = computed(() => [
  { key: 'dmarc', label: 'DMARC', value: dmarcRate.value, color: 'rgb(var(--cr-beam))' },
  { key: 'dkim', label: 'DKIM', value: dkimRate.value, color: 'rgb(var(--cr-plum))' },
  { key: 'spf', label: 'SPF', value: spfRate.value, color: 'rgb(var(--cr-flare))' },
]);

const rateTone = (value: number) =>
  value >= 80 ? 'text-gray-900' : value >= 50 ? 'text-warning-700' : 'text-danger-700';

/**
 * One sentence that names the thing worth looking at: the weakest layer first, then a domain
 * whose DNS check failed, then the all-clear. It deliberately stops short of advice - the
 * dashboard reports what the reports say, not what the policy should be.
 */
const insight = computed(() => {
  const warnClasses = 'border-warning-200 bg-warning-50 text-warning-800';
  const okClasses = 'border-success-200 bg-success-50 text-success-800';

  const dmarc = dmarcRate.value;
  const dkim = dkimRate.value;
  const spf = spfRate.value;
  const failures = (domains.value?.error ?? 0) + (domains.value?.warn ?? 0);

  if (spf < 50 && dkim < 50) {
    return {
      tone: 'warn',
      classes: warnClasses,
      text: `SPF passes on ${spf}% and DKIM on ${dkim}% of messages, so DMARC is passing on ${dmarc}%. Both mechanisms need attention.`,
    };
  }
  if (spf < 50) {
    return {
      tone: 'warn',
      classes: warnClasses,
      text: `SPF passes on ${spf}% of messages. Every DMARC pass in this window is riding on DKIM.`,
    };
  }
  if (dkim < 50) {
    return {
      tone: 'warn',
      classes: warnClasses,
      text: `DKIM passes on ${dkim}% of messages. Every DMARC pass in this window is riding on SPF.`,
    };
  }
  if (failures > 0) {
    return {
      tone: 'warn',
      classes: warnClasses,
      text: `${failures} domain${failures === 1 ? '' : 's'} reported a problem on the last DNS check. The rates above only describe mail that arrived.`,
    };
  }
  return {
    tone: 'ok',
    classes: okClasses,
    text: 'Every protocol layer is passing in this window, and every monitored domain passed its last check.',
  };
});

const tiles = computed(() => {
  const dmarc = dmarcRate.value;
  const ok = domains.value?.ok ?? 0;
  const total = domains.value?.total ?? 0;
  const problems = (domains.value?.warn ?? 0) + (domains.value?.error ?? 0);

  return [
    {
      label: 'Aggregate reports',
      value: (summary.value?.totalReports ?? 0).toLocaleString(),
      suffix: '',
      meter: null,
      color: 'rgb(var(--cr-beam))',
      footnote: `${(summary.value?.totalMessages ?? 0).toLocaleString()} messages`,
    },
    {
      label: 'Messages seen',
      value: (summary.value?.totalMessages ?? 0).toLocaleString(),
      suffix: '',
      meter: dmarc,
      color: 'rgb(var(--cr-beam))',
      footnote: `${dmarc}% passed DMARC`,
    },
    {
      label: 'Domain perimeter',
      value: `${ok}/${total}`,
      suffix: '',
      meter: total > 0 ? Math.round((ok / total) * 100) : 0,
      color: problems > 0 ? 'rgb(var(--cr-flare))' : 'rgb(var(--cr-good))',
      footnote:
        total === 0
          ? 'No domains monitored'
          : problems === 0
            ? 'All checks nominal'
            : `${domains.value?.warn ?? 0} warn · ${domains.value?.error ?? 0} error`,
    },
    {
      label: 'Forensic channel',
      value: (forensic.value?.total ?? 0).toLocaleString(),
      suffix: '',
      meter: null,
      color: 'rgb(var(--cr-plum))',
      footnote: `RUF ${forensic.value?.last24h ?? 0} in 24h`,
    },
  ];
});

const trendPoints = computed<TelemetryPoint[]>(() =>
  (summary.value?.trendsData ?? []).map((point) => ({
    date: point.date,
    messageCount: point.messageCount,
    failureRate: point.failureRate,
  })),
);

const policy = computed(() => {
  const distribution = summary.value?.policyDistribution ?? { none: 0, quarantine: 0, reject: 0 };
  const total = distribution.none + distribution.quarantine + distribution.reject;
  const pct = (value: number) => (total > 0 ? Math.round((value / total) * 100) : 0);

  return {
    nonePct: pct(distribution.none),
    segments: [
      { key: 'none', label: 'None', value: distribution.none, pct: pct(distribution.none), color: 'rgb(var(--cr-beam))' },
      { key: 'quarantine', label: 'Quarantine', value: distribution.quarantine, pct: pct(distribution.quarantine), color: 'rgb(var(--cr-plum))' },
      { key: 'reject', label: 'Reject', value: distribution.reject, pct: pct(distribution.reject), color: 'rgb(var(--cr-flare))' },
    ],
  };
});

const topSources = computed(() => (summary.value?.topSources ?? []).slice(0, 5));
const topIPs = computed(() => (summary.value?.topIPs ?? []).slice(0, 5));
const needsAttention = computed(() => (domains.value?.needsAttention ?? []).slice(0, 5));
const spoofingSources = computed(() => (forensic.value?.topSources ?? []).slice(0, 5));

const maxOrgMessages = computed(() =>
  topSources.value.reduce((max, org) => Math.max(max, org.messageCount), 0),
);

const share = (value: number, max: number) => (max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0);
</script>

<style scoped>
/* v4: a scoped block is its own compilation unit, so it must be pointed at the theme. */
@reference "../style.css";

/*
 * The control-room palette. These are dashboard-local on purpose: the rest of the app keeps
 * the neutral tokens, while this page needs a signal colour (beam), a second series (plum)
 * and a warning/spike colour (flare). Both themes define the same three names, so the SVG
 * chart and gauge read them from CSS instead of re-resolving colours on every theme flip.
 */
.control-room {
  --cr-beam: 2 132 199;
  --cr-plum: 109 40 217;
  --cr-flare: 180 83 9;
  --cr-good: 4 120 87;
  --cr-grid: 15 23 42;
  --cr-dot: 15 23 42;

  /* A faint dot grid: it is what makes the dark surface read as an instrument panel rather
     than an empty page, and it is barely visible in light mode. */
  background-image: radial-gradient(rgb(var(--cr-dot) / 0.1) 1px, transparent 1px);
  background-size: 24px 24px;
}

.dark .control-room {
  --cr-beam: 56 219 245;
  --cr-plum: 167 139 250;
  --cr-flare: 251 191 36;
  --cr-good: 52 211 153;
  --cr-grid: 148 163 184;
  --cr-dot: 148 163 184;
}

.cr-card {
  @apply rounded-2xl border border-gray-200 bg-surface;
  box-shadow: inset 0 1px 0 0 rgb(255 255 255 / 0.04);
}

.cr-label {
  @apply text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500;
}

.cr-link {
  @apply text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500 transition-colors hover:text-gray-900;
}

.cr-chip {
  @apply inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-600;
}

.cr-icon-button {
  @apply inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-surface text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-primary-500;
}

@media (prefers-reduced-motion: reduce) {
  .control-room :deep(.animate-ping) {
    animation: none;
  }
}
</style>
